import { useMemo, useState, type FormEvent } from 'react';
import {
  V2Card,
  V2CardHeader,
  V2Button,
  V2FormGrid,
  V2FormField,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import type { AccountListResponse, FiscalPeriodListResponse } from '../../lib/types/finance.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import type { AmountDisplayUnit } from '../../lib/finance-display.js';
import { previewInvoice } from '../../lib/invoice-preview.js';
import { CHARGE_TYPE_LABEL } from '../../lib/types/finance.js';
import {
  EMPTY_INVOICE_LINE,
  InvoiceLinesEditor,
  taxRateLabel,
  type InvoiceFormLine,
} from './InvoiceLinesEditor.js';

/**
 * UI_REFERENCE_SOURCE:
 * section: Finance.dc.html — AR/AP manual open-item entry (دریافتنی/پرداختنی
 *   دستی); the v2 AR/AP frames show no entry form, so this stays the existing
 *   real form, opened from the page header action.
 * status: PARITY with the pre-redesign page — unchanged behaviour, moved out of
 *   OpenItemsPage so the page can follow the v2 balances/aging/list structure.
 *   POST .../finance/{ar|ap}/open-items (apps/api/src/finance/ar.controller.ts
 *   `create`) with an Idempotency-Key header (added by apiPost).
 */
export function OpenItemCreateCard({
  kind,
  slug,
  base,
  unit,
  unitLabel,
  onCreated,
}: {
  readonly kind: 'ar' | 'ap';
  readonly slug: string;
  readonly base: string;
  readonly unit: AmountDisplayUnit;
  readonly unitLabel: string;
  readonly onCreated: () => void;
}) {
  const createTitle =
    kind === 'ar' ? 'ثبت دریافتنی جدید (فاکتور فروش دستی)' : 'ثبت پرداختنی جدید (فاکتور خرید دستی)';
  const glAccounts = useApiGet<AccountListResponse>(`${base}/api/v1/finance/accounts`, [slug]);
  const fiscalPeriods = useApiGet<FiscalPeriodListResponse>(
    `${base}/api/v1/accounting/fiscal-periods`,
    [slug],
  );
  const partyRole = kind === 'ar' ? 'customer' : 'supplier';
  const parties = useApiGet<MasterDataListResponse>(
    `${base}/api/v1/master-data/parties?role=${partyRole}&limit=200`,
    [slug, partyRole],
  );
  const postableGlAccounts = useMemo(
    () =>
      glAccounts.status === 'ready'
        ? [...glAccounts.data.items]
            .filter((a) => a.isPostable && a.status === 'active')
            .sort((a, b) => a.code.localeCompare(b.code))
        : [],
    [glAccounts],
  );

  // --- create manual open item ---
  const [ciPartyId, setCiPartyId] = useState('');
  const [ciSourceRef, setCiSourceRef] = useState('');
  const [ciDate, setCiDate] = useState('');
  const [ciDueDate, setCiDueDate] = useState('');
  const [ciFiscalPeriodId, setCiFiscalPeriodId] = useState('');
  const [ciLines, setCiLines] = useState<InvoiceFormLine[]>([{ ...EMPTY_INVOICE_LINE }]);
  const [ciDocDiscount, setCiDocDiscount] = useState('');
  const [ciTaxId, setCiTaxId] = useState('');
  const [ciVatAccountId, setCiVatAccountId] = useState('');
  const [ciControlAccountId, setCiControlAccountId] = useState('');
  const [ciCounterpartAccountId, setCiCounterpartAccountId] = useState('');
  const [ciDescription, setCiDescription] = useState('');
  const [creatingItem, setCreatingItem] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const taxes = useApiGet<MasterDataListResponse>(
    `${base}/api/v1/master-data/taxes?status=active`,
    [slug],
  );
  const products = useApiGet<MasterDataListResponse>(
    `${base}/api/v1/master-data/products?status=active`,
    [slug],
  );
  const selectedTax =
    taxes.status === 'ready'
      ? taxes.data.items.find((t) => String(t.taxId) === ciTaxId)
      : undefined;
  const preview = useMemo(
    () =>
      previewInvoice({
        lines: ciLines,
        documentDiscountRial: ciDocDiscount,
        taxRate: selectedTax
          ? {
              numerator: BigInt(String(selectedTax.rateNumerator)),
              denominator: BigInt(String(selectedTax.rateDenominator)),
            }
          : null,
      }),
    [ciLines, ciDocDiscount, selectedTax],
  );

  function updateCiLine(index: number, patch: Partial<InvoiceFormLine>) {
    setCiLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  }

  async function onCreateItem(event: FormEvent) {
    event.preventDefault();
    setCreateError(null);
    const period =
      fiscalPeriods.status === 'ready'
        ? fiscalPeriods.data.items.find((p) => p.fiscalPeriodId === ciFiscalPeriodId)
        : undefined;
    if (
      !ciPartyId ||
      !ciSourceRef.trim() ||
      !ciDate ||
      !ciDueDate ||
      !period ||
      !ciControlAccountId ||
      !ciCounterpartAccountId
    ) {
      setCreateError('همهٔ فیلدهای الزامی را تکمیل کنید.');
      return;
    }
    const filledLines = ciLines.filter((l) => l.description.trim() || l.unitPriceRial);
    if (filledLines.length === 0) {
      setCreateError('حداقل یک ردیف کالا/خدمت با شرح و مبلغ لازم است.');
      return;
    }
    if (filledLines.some((l) => !l.description.trim() || !l.unitPriceRial)) {
      setCreateError('هر ردیف به شرح و فی نیاز دارد.');
      return;
    }
    if (filledLines.some((l) => l.lineKind === 'charge' && !l.chargeType)) {
      setCreateError('نوع هزینهٔ جانبی را برای هر ردیف هزینه انتخاب کنید.');
      return;
    }
    if (preview.error) {
      setCreateError(preview.error);
      return;
    }
    if (ciTaxId && preview.vatRial > 0n && !ciVatAccountId) {
      setCreateError('برای محاسبهٔ ارزش افزوده، حساب مالیات بر ارزش افزوده را انتخاب کنید.');
      return;
    }
    setCreatingItem(true);
    try {
      await apiPost(`${base}/api/v1/finance/${kind}/open-items`, {
        partyId: ciPartyId,
        sourceType: 'manual',
        sourceReference: ciSourceRef.trim(),
        businessDate: ciDate,
        dueDate: ciDueDate,
        fiscalYearId: period.fiscalYearId,
        fiscalPeriodId: period.fiscalPeriodId,
        controlAccountId: ciControlAccountId,
        counterpartAccountId: ciCounterpartAccountId,
        description: ciDescription.trim() || undefined,
        ...(ciTaxId ? { taxId: ciTaxId } : {}),
        ...(ciTaxId && preview.vatRial > 0n ? { vatAccountId: ciVatAccountId } : {}),
        ...(ciDocDiscount && ciDocDiscount !== '0' ? { documentDiscountRial: ciDocDiscount } : {}),
        lines: filledLines.map((l) => ({
          lineKind: l.lineKind,
          ...(l.lineKind === 'charge' ? { chargeType: l.chargeType } : {}),
          ...(l.lineKind === 'item' && l.productId ? { productId: l.productId } : {}),
          description: l.description.trim(),
          quantity: l.quantity.trim() || '1',
          unitPriceRial: l.unitPriceRial,
          ...(l.lineKind === 'item' && l.discountRial ? { discountRial: l.discountRial } : {}),
          ...(l.accountId ? { accountId: l.accountId } : {}),
        })),
      });
      setCiPartyId('');
      setCiSourceRef('');
      setCiDate('');
      setCiDueDate('');
      setCiFiscalPeriodId('');
      setCiLines([{ ...EMPTY_INVOICE_LINE }]);
      setCiDocDiscount('');
      setCiTaxId('');
      setCiVatAccountId('');
      setCiControlAccountId('');
      setCiCounterpartAccountId('');
      setCiDescription('');
      onCreated();
    } catch {
      setCreateError('ثبت ناموفق بود — فیلدهای الزامی و اعتبار حساب‌ها را بررسی کنید.');
    } finally {
      setCreatingItem(false);
    }
  }

  return (
    <V2Card>
      <V2CardHeader title={createTitle} />
      <form
        onSubmit={onCreateItem}
        style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <V2FormGrid>
          <V2FormField label="طرف‌حساب" required width="wide">
            <select
              className="sk-select"
              value={ciPartyId}
              onChange={(e) => setCiPartyId(e.target.value)}
            >
              <option value="">انتخاب طرف‌حساب</option>
              {parties.status === 'ready' &&
                parties.data.items.map((p) => (
                  <option key={String(p.partyId)} value={String(p.partyId)}>
                    {String(p.code)} — {String(p.name)}
                  </option>
                ))}
            </select>
          </V2FormField>
          <V2FormField label="شمارهٔ فاکتور/سند" required width="medium">
            <input
              className="sk-input ltr"
              value={ciSourceRef}
              onChange={(e) => setCiSourceRef(e.target.value)}
            />
          </V2FormField>
          <V2FormField label="تاریخ سند" required width="medium">
            <JalaliDateInput value={ciDate} onChange={setCiDate} />
          </V2FormField>
          <V2FormField label="سررسید" required width="medium">
            <JalaliDateInput value={ciDueDate} onChange={setCiDueDate} />
          </V2FormField>
          <V2FormField label="دورهٔ مالی" required width="medium">
            <select
              className="sk-select"
              value={ciFiscalPeriodId}
              onChange={(e) => setCiFiscalPeriodId(e.target.value)}
            >
              <option value="">انتخاب دوره</option>
              {fiscalPeriods.status === 'ready' &&
                fiscalPeriods.data.items.map((p) => (
                  <option
                    key={p.fiscalPeriodId}
                    value={p.fiscalPeriodId}
                    disabled={p.postingState !== 'open'}
                  >
                    {p.name} {p.postingState !== 'open' ? '· بسته' : ''}
                  </option>
                ))}
            </select>
          </V2FormField>
          <V2FormField
            label="مالیات بر ارزش افزوده"
            width="medium"
            hint="نرخ از اطلاعات پایه؛ مالیات هر ردیف خودکار محاسبه می‌شود"
          >
            <select
              className="sk-select"
              value={ciTaxId}
              onChange={(e) => setCiTaxId(e.target.value)}
            >
              <option value="">بدون مالیات</option>
              {taxes.status === 'ready' &&
                taxes.data.items.map((t) => (
                  <option key={String(t.taxId)} value={String(t.taxId)}>
                    {String(t.name)} ({taxRateLabel(t)})
                  </option>
                ))}
            </select>
          </V2FormField>
          {ciTaxId ? (
            <V2FormField label="حساب مالیات بر ارزش افزوده" required width="wide">
              <select
                className="sk-select"
                value={ciVatAccountId}
                onChange={(e) => setCiVatAccountId(e.target.value)}
              >
                <option value="">انتخاب حساب</option>
                {postableGlAccounts.map((a) => (
                  <option key={a.accountId} value={a.accountId}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </select>
            </V2FormField>
          ) : null}
          <V2FormField
            label={kind === 'ar' ? 'حساب کنترل دریافتنی' : 'حساب کنترل پرداختنی'}
            required
            width="wide"
          >
            <select
              className="sk-select"
              value={ciControlAccountId}
              onChange={(e) => setCiControlAccountId(e.target.value)}
            >
              <option value="">انتخاب حساب</option>
              {postableGlAccounts.map((a) => (
                <option key={a.accountId} value={a.accountId}>
                  {a.code} — {a.name}
                </option>
              ))}
            </select>
          </V2FormField>
          <V2FormField
            label="حساب معین مقابل"
            required
            width="wide"
            hint="مثلاً درآمد فروش یا هزینهٔ خرید"
          >
            <select
              className="sk-select"
              value={ciCounterpartAccountId}
              onChange={(e) => setCiCounterpartAccountId(e.target.value)}
            >
              <option value="">انتخاب حساب</option>
              {postableGlAccounts.map((a) => (
                <option key={a.accountId} value={a.accountId}>
                  {a.code} — {a.name}
                </option>
              ))}
            </select>
          </V2FormField>
          <V2FormField label="شرح" width="full">
            <input
              className="sk-input"
              value={ciDescription}
              onChange={(e) => setCiDescription(e.target.value)}
            />
          </V2FormField>
        </V2FormGrid>

        <InvoiceLinesEditor
          kind={kind}
          lines={ciLines}
          preview={preview}
          unitLabel={unitLabel}
          unit={unit}
          accounts={postableGlAccounts}
          products={products.status === 'ready' ? products.data.items : []}
          documentDiscount={ciDocDiscount}
          onDocumentDiscount={setCiDocDiscount}
          onChange={updateCiLine}
          onAdd={(lineKind) =>
            setCiLines((prev) => [
              ...prev,
              {
                ...EMPTY_INVOICE_LINE,
                lineKind,
                ...(lineKind === 'charge'
                  ? { chargeType: 'freight' as const, description: CHARGE_TYPE_LABEL.freight }
                  : {}),
              },
            ])
          }
          onRemove={(index) => setCiLines((prev) => prev.filter((_, i) => i !== index))}
        />
        {createError ? (
          <div style={{ fontSize: 13, color: 'var(--sk-danger)' }}>{createError}</div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <V2Button type="submit" variant="primary" icon="plus" disabled={creatingItem}>
            {creatingItem ? 'در حال ثبت…' : 'ثبت'}
          </V2Button>
        </div>
      </form>
    </V2Card>
  );
}
